import { useRef, FC } from 'react'
import styles from 'components/features/external/PublicDashboardPage/PublicDashboardPage.styl'
import cn from 'classnames'
import Anchor from 'components/atoms/navigation/Anchor'
import ExternalLayout from 'components/features/external/ExternalLayout/ExternalLayout'
import ExternalHeader from 'components/features/external/ExternalHeader/ExternalHeader'
import { ExternalPageHeading } from 'components/atoms/Text'
import { PublicSpendingTrend } from 'components/features/external/PublicSpendingTrend/PublicSpendingTrend'
import { PublicDashboardModuleWrapper } from 'components/features/external/PublicDashboardModuleWrapper/PublicDashboardModuleWrapper'
import { PublicSpendingByStateChart } from 'components/features/external/PublicSpendingByStateChart/PublicSpendingByStateChart'
import { PublicSpendingFilters } from 'components/features/external/PublicSpendingFilters/PublicSpendingFilters'
import { PublicSpendingByCategory } from 'components/features/external/PublicSpendingByCategory/PublicSpendingByCategory'
import { PublicSpendingByMerchant } from 'components/features/external/PublicSpendingByMerchant/PublicSpendingByMerchant'
import { useShouldShowPageParamsStickyHeader } from 'helpers/pageHelpers'
import StickyHeader from 'components/unorganized/StickyHeader'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'
import { isFeatureEnabled } from 'helpers/featureHelpers'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'
import { HowSpendingCompare } from 'components/features/publicDashboard/HowSpendingCompare/HowSpendingCompare'

const MODULE_SUBTITLE = 'Last 2 weeks vs. Jan 2020'

type PublicDashboardPageProps = {

}

export const PublicDashboardPage: FC<PublicDashboardPageProps> = (props) => {
  const {} = props
  const filterRef = useRef<HTMLDivElement>(null)
  const shouldShowStickyHeader = useShouldShowPageParamsStickyHeader(filterRef)
  const headerChild = (
    <ExternalHeader
      theme='light'
      hasExternalNavigation
      hasLoginButton
      hasJoinButton
      isSticky={ false }
    />
  )

  const isPublicSpendingTrendFilterEnabled = isFeatureEnabled('public_spending_trend_filter')

  return (
    <Anchor placement='publicdashboard'>
      <PageMeta
        title='Compare Your Spending'
        description='Track consumer spending daily | Compare your spending'
      />
      <ExternalLayout
        type='common'
        headerNode={ headerChild }
        isSingleColumn
        noStickyHeader
        wide
      >
        <StickyHeader isScrolled={ shouldShowStickyHeader } className={ styles.stickyHeader }>
          <div className={ styles.filters }>
            <PublicSpendingFilters />
          </div>
        </StickyHeader>
        <section className={ styles.heading }>
          <ExternalPageHeading>Economic Trends</ExternalPageHeading>
        </section>
        <div className={ styles.filters } ref={ filterRef }>
          <PublicSpendingFilters />
        </div>
        <div className={ styles.grid }>
          <div className={ cn(styles.trend, styles.cell) }>
            <PublicDashboardModuleWrapper
              title='Spending per Consumer'
              noPadding
            >
              <div className={ cn(styles.trendContentWrapper, isPublicSpendingTrendFilterEnabled && styles.trendContentWrapperWithFilter) }>
                <PublicSpendingTrend />
              </div>
            </PublicDashboardModuleWrapper>
          </div>
          <div className={ cn(styles.howSpendingCompare, styles.cell) }>
            <HowSpendingCompare />
          </div>
          <div className={ cn(styles.byState, styles.cell) }>
            <PublicDashboardModuleWrapper
              title='Spending Change by State'
              subtitle={ MODULE_SUBTITLE }
              noPadding
            >
              <PublicSpendingByStateChart />
            </PublicDashboardModuleWrapper>
          </div>
          <div className={ cn(styles.byCategory, styles.cell) }>
            <PublicDashboardModuleWrapper
              title='Spending Change by Category'
              subtitle={ MODULE_SUBTITLE }
            >
              <PublicSpendingByCategory />
            </PublicDashboardModuleWrapper>
          </div>
          <div className={ cn(styles.byMerchant, styles.cell) }>
            <PublicDashboardModuleWrapper
              title='Spending Change for Top Merchants'
              subtitle={ MODULE_SUBTITLE }
            >
              <PublicSpendingByMerchant />
            </PublicDashboardModuleWrapper>
          </div>
          <div className={ cn(styles.disclaimer, styles.cell) }>
            <p>The information on this page is provided solely to raise awareness about the financial impact of the COVID-19 crisis on people and businesses.
              <br className={ styles.hideOnMobile } /> It will be updated daily at midnight eastern standard time.
            </p>
            <p>The data is based on a proprietary panel that covers more than a million people across the United States.</p>
            <p>You may distribute and reprint this information for non-commercial purposes with attribution to Status Money and a link to this page.</p>
            <p>For other inquiries, please get in touch <EmailLink email='contact@statusmoney.com' />.</p>
          </div>
        </div>
      </ExternalLayout>
    </Anchor>
  )
}
